import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type {
	ExtensionManifest,
	ExtensionModule,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import Lozenge from '@atlaskit/lozenge/lozenge';

import { ANALYTICS_ORIGIN } from '../constants/snippetAnalytics.ts';
import {
	buildInsertSnippetEventData,
	fireSnippetUIEvent,
} from '../hooks/analytics/snippetAnalyticsPayload.ts';
import type { Snippet } from '../types/snippet.ts';
import {
	extractSnippetKeywords,
	createSnippetDescription,
	filterAndSort,
} from '../utils/quickInsertUtils.ts';

const messages = defineMessages({
	snippetsQuickInsertTitle: {
		id: 'snippets.snippetsQuickInsertTitle',
		defaultMessage: 'Block Template Gallery',
		description: 'Title for the block template quick insert item',
	},
	newLozenge: {
		id: 'snippets.quickInsert.newLozenge',
		defaultMessage: 'New',
		description: 'Text in lozenge next to the Block Template quick insert item',
	},
	newLozengeUpdated: {
		id: 'snippets.quickInsert.newLozenge.updated',
		defaultMessage: 'New',
		description: 'Sentence case text in lozenge next to the Block Template quick insert item',
	},
	snippetIconLabel: {
		id: 'snippets.snippetIconLabel',
		defaultMessage: 'Block template icon',
		description:
			'The text is used as the accessible label for the block template icon displayed in the quick-insert menu or extension manifest, describing the icon visually for assistive technologies.',
	},
	snippetQuickInsertDescription: {
		id: 'snippets.snippetQuickInsertDescription',
		defaultMessage: 'Create, discover, & insert block templates',
		description: 'Description for the block template quick insert item',
	},
});

type CreateSnippetExtensionProps = {
	snippets: Snippet[];
	intl: IntlShape;
	onOpenTemplatePanel?: () => void;
	userId?: string | null;
	createAnalyticsEvent?: CreateUIAnalyticsEvent | null;
};

/**
 * Fires an `insertSnippet` UI event with `origin=quickInsert` when a snippet is
 * inserted from the quick-insert menu via the extension-provider path.
 *
 * The extension `action` API does not expose analytics, so the
 * `createAnalyticsEvent` factory is captured from the build site (where the
 * editor's analytics context is available) and used here. No-op when the
 * factory is unavailable — keeping the event shape identical to the imperative
 * `createSnippetQuickInsertItem` path.
 */
const fireQuickInsertSnippetInsertedAnalytic = (
	createAnalyticsEvent: CreateUIAnalyticsEvent | null | undefined,
	snippet: Snippet,
) => {
	fireSnippetUIEvent(
		createAnalyticsEvent,
		buildInsertSnippetEventData(snippet),
		ANALYTICS_ORIGIN.QUICK_INSERT,
	);
};

export const snippetExtensionManifest = ({
	snippets,
	intl,
	onOpenTemplatePanel,
	userId,
	createAnalyticsEvent,
}: CreateSnippetExtensionProps): ExtensionManifest => {
	const snippetsQuickInsertTitle = intl.formatMessage(messages.snippetsQuickInsertTitle);
	const snippetIconLabel = intl.formatMessage(messages.snippetIconLabel);
	const snippetQuickInsertDescription = intl.formatMessage(messages.snippetQuickInsertDescription);
	const quickInsert: ExtensionModule[] = filterAndSort(snippets, userId).map((snippet) => ({
		key: `snippet-${snippet.snippetId}`,
		title: snippet.name,
		description: createSnippetDescription(snippet.createdBy, intl),
		keywords: extractSnippetKeywords(intl),
		icon: async () => () => <TemplateIcon label={snippetIconLabel} />,
		action: async () => {
			// Only record an insert event when there is a body to insert, matching
			// the imperative `createSnippetQuickInsertItem` path which guards on a
			// successful insert before firing.
			if (snippet.body) {
				fireQuickInsertSnippetInsertedAnalytic(createAnalyticsEvent, snippet);
			}
			return snippet.body;
		},
	}));
	// Add "Block Template" quick insert item if callback is provided
	if (onOpenTemplatePanel) {
		quickInsert.unshift({
			key: 'block-template',
			title: snippetsQuickInsertTitle,
			description: snippetQuickInsertDescription,
			keywords: extractSnippetKeywords(intl),
			icon: async () => () => <TemplateIcon label={snippetIconLabel} />,
			lozenge: (
				<Lozenge appearance="discovery">{intl.formatMessage(messages.newLozengeUpdated)}</Lozenge>
			),
			action: async () => {
				onOpenTemplatePanel();
			},
		});
	}

	return {
		title: snippetsQuickInsertTitle,
		description: snippetQuickInsertDescription,
		type: 'com.atlassian.confluence.macro.core',
		key: 'snippet-extension',
		icons: {
			// eslint-disable-next-line import/dynamic-import-chunkname
			'48': () => import('@atlaskit/icon-lab/core/template'),
		},
		modules: {
			quickInsert,
		},
	};
};
